/** Plan form helpers for the Super Admin Plans page. API prices are paise, before GST. */

import { formatInr } from './billingAdmin';

export const GST_RATE_PERCENT = 18;
export const MAX_WEBSITE_BULLETS = 8;
export const MAX_WEBSITE_BULLET_LENGTH = 80;

/** Paise → rupees string for a form input ('' when unset). */
export function toMajorInput(minor) {
  if (minor == null || minor === '') return '';
  const n = Number(minor);
  if (!Number.isFinite(n)) return '';
  return String(n / 100);
}

/** Rupees → paise for the API; blank → null. */
export function toMinorUnits(major) {
  if (major === '' || major == null) return null;
  const n = Number(major);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error('Price must be a non-negative number');
  }
  return Math.round(n * 100);
}

/** Whole-rupee price for tables: "₹1,799" or "—". */
export function formatPlanPrice(paise) {
  if (paise == null) return '—';
  const whole = Number(paise) % 100 === 0;
  return formatInr(paise, { decimals: !whole });
}

/**
 * What the yearly price means for a buyer, from the form's rupee inputs.
 * @returns {string|null} e.g. "₹559/month · 20% off monthly"
 */
export function yearlyPriceHint(monthlyMajor, yearlyMajor) {
  const monthly = Number(monthlyMajor);
  const yearly = Number(yearlyMajor);
  if (!yearlyMajor || !Number.isFinite(yearly) || yearly <= 0) return null;
  const perMonth = Math.round(yearly / 12);
  const perMonthText = `₹${perMonth.toLocaleString('en-IN')}/month`;
  if (!monthlyMajor || !Number.isFinite(monthly) || monthly <= 0) return perMonthText;
  const saving = Math.round((1 - yearly / (monthly * 12)) * 100);
  if (saving > 0) return `${perMonthText} · ${saving}% off monthly`;
  if (saving < 0) return `${perMonthText} · costs more than paying monthly`;
  return `${perMonthText} · same as monthly`;
}

/**
 * What checkout charges for a pre-tax rupee price.
 * @returns {string|null} e.g. "₹824.82"
 */
export function priceWithGst(major, ratePercent = GST_RATE_PERCENT) {
  const price = Number(major);
  if (!major || !Number.isFinite(price) || price <= 0) return null;
  const net = Math.round(price * 100);
  const total = net + Math.round((net * ratePercent) / 100);
  return formatInr(total, { decimals: total % 100 !== 0 });
}

/** @returns {string|null} e.g. "Customer pays ₹824.82 with 18% GST" */
export function withGstHint(major, ratePercent = GST_RATE_PERCENT) {
  const total = priceWithGst(major, ratePercent);
  return total ? `Customer pays ${total} with ${ratePercent}% GST` : null;
}

/** Yearly total that gives `percent`% off twelve monthly payments, in whole rupees. */
export function yearlyAtDiscount(monthlyMajor, percent = 20) {
  const monthly = Number(monthlyMajor);
  if (!Number.isFinite(monthly) || monthly <= 0) return '';
  return String(Math.round(monthly * (1 - percent / 100)) * 12);
}

export function bulletsToText(bullets) {
  return Array.isArray(bullets) ? bullets.join('\n') : '';
}

export function textToBullets(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Problem with the bullet list, or null when it's fine. */
export function websiteBulletsError(text) {
  const bullets = textToBullets(text);
  if (bullets.length > MAX_WEBSITE_BULLETS) {
    return `Use at most ${MAX_WEBSITE_BULLETS} bullet points (you have ${bullets.length}).`;
  }
  const long = bullets.find((line) => line.length > MAX_WEBSITE_BULLET_LENGTH);
  if (long) return `Keep each bullet under ${MAX_WEBSITE_BULLET_LENGTH} characters.`;
  return null;
}
